import { dirname, join, parse, relative } from 'node:path';
import { globby } from 'globby';
import { pathExists, relativeFolderName } from '@wix/cli-fs';
import type { WixDependency } from '@wix/cli-app-manifest';
import type {
  WixConfig,
  SiteComponentElementItem,
} from '@wix/cli-app-definitions';
import {
  EMBEDDED_SCRIPTS_DIR,
  getSiteWidgetsDir,
  PlatformType,
  SITE_WIDGETS_DIR,
  SITE_PLUGINS_DIR,
  getSitePluginsDir,
  BLOCKS_WIDGETS_DIR,
  RELATIVE_ASSET_PATH_REGEX,
  SITE_COMPONENTS_DIR,
  PANELS_DIR,
  PRESETS_DIR,
  ELEMENTS_DIR,
} from '@wix/cli-app-definitions';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { randomUUID } from 'node:crypto';
import { ElementType } from '@wix/ambassador-devcenter-apps-v1-app-old/types';
import { readAppConfig } from '../../../files/app.config.js';
import { normalizeGlobbyPattern } from '../path.js';
import {
  loadEmbeddedScriptComponentConfig,
  loadEmbeddedScriptParamsOverride,
  loadCustomElementComponentConfig,
  loadSitePluginComponentConfig,
  loadBlocksWidgetComponentConfig,
  loadBlocksWidgetPanelComponentConfig,
  loadSiteComponentConfig,
  loadSiteComponentPanelConfig,
  loadSiteComponentElementConfig,
} from './config.js';
import {
  createEmbeddedScriptFolderValidation,
  validateCustomElementName,
  validateEmbeddedScriptHtml,
} from '../validations/index.js';
import { DevCenterComponent } from '../dev-center-component.js';

export const EMBEDDED_SCRIPTS_PATTERN = join(
  EMBEDDED_SCRIPTS_DIR,
  '**',
  'embedded.html'
);

const SDK_PATTERN = 'sdk.{ts,js}';
const PREVIEW_PATTERN = 'component.preview.{ts,tsx,js,jsx}';

export const SITE_COMPONENTS_PATTERN = join(
  SITE_COMPONENTS_DIR,
  '**',
  'component.{ts,tsx,js,jsx}'
);

export const SITE_COMPONENTS_PREVIEW_PATTERN = join(
  SITE_COMPONENTS_DIR,
  '**',
  PREVIEW_PATTERN
);

const PANEL_PATTERN = join(PANELS_DIR, '**', 'panel.{ts,tsx,js,jsx}');

const ELEMENT_PATTERN = join(ELEMENTS_DIR, '*', 'element.json');
const PRESET_PATTERN = join(PRESETS_DIR, '*.css');

export const SITE_COMPONENTS_PANEL_PATTERN = join(
  SITE_COMPONENTS_DIR,
  '**',
  PANEL_PATTERN
);

export const SITE_COMPONENTS_PRESETS_PATTERN = join(
  SITE_COMPONENTS_DIR,
  '*',
  PRESET_PATTERN
);

export const SITE_COMPONENTS_SDK_PATTERN = join(
  SITE_COMPONENTS_DIR,
  '**',
  SDK_PATTERN
);

export const SITE_WIDGETS_ELEMENT_PATTERN = join(
  SITE_WIDGETS_DIR,
  '**',
  'element.{ts,tsx,js,jsx}'
);

export const SITE_WIDGETS_PANEL_PATTERN = join(
  SITE_WIDGETS_DIR,
  '**',
  'panel.{ts,tsx,js,jsx}'
);

export const SITE_PLUGINS_PLUGIN_PATTERN = join(
  SITE_PLUGINS_DIR,
  '**',
  'plugin.{ts,tsx,js,jsx}'
);

export const SITE_PLUGINS_PANEL_PATTERN = join(
  SITE_PLUGINS_DIR,
  '**',
  'panel.{ts,tsx,js,jsx}'
);

async function loadCustomElementComponent(
  customElementPath: string,
  projectFolder: string,
  wixFrontendDependencies: WixDependency[]
): Promise<DevCenterComponent<'CustomElement'>> {
  const customElementConfig =
    await loadCustomElementComponentConfig(customElementPath);
  const customElementPanelPath = join(dirname(customElementPath), 'panel.tsx');
  const tagName = relativeFolderName(
    getSiteWidgetsDir(projectFolder),
    customElementPath
  );

  const isPanelExists = await pathExists(
    join(projectFolder, customElementPanelPath)
  );

  if (
    customElementConfig.presets?.some((preset) =>
      RELATIVE_ASSET_PATH_REGEX.test(preset.thumbnailUrl)
    )
  ) {
    const customElementDir = dirname(customElementPath);
    const assetPaths = customElementConfig.presets
      .filter((preset) => RELATIVE_ASSET_PATH_REGEX.test(preset.thumbnailUrl))
      .map((preset) =>
        join(projectFolder, customElementDir, preset.thumbnailUrl)
      );

    for (const assetPath of assetPaths) {
      if (!(await pathExists(assetPath))) {
        throw new CliError({
          code: CliErrorCode.CannotFindConfigReferencedAsset({
            asset: assetPath,
            configPath: `${customElementDir}/element.json`,
            field: 'presets.thumbnailUrl',
          }),
          cause: null,
        });
      }
    }
  }

  return DevCenterComponent.CustomElement({
    ...customElementConfig,
    tagName,
    relativePath: customElementPath,
    platforms: [PlatformType.Site(), PlatformType.Editor()],
    ...(isPanelExists
      ? // relative paths should be posix style, as they are used in JS for import statements
        { relativePanelPath: normalizeGlobbyPattern(customElementPanelPath) }
      : {}),
    dependencies: wixFrontendDependencies,
  });
}

export async function loadCustomElementComponents(
  projectFolder: string,
  wixFrontendDependencies: WixDependency[]
): Promise<Array<DevCenterComponent<'CustomElement'>>> {
  const customElementsPaths = await globby(
    normalizeGlobbyPattern(SITE_WIDGETS_ELEMENT_PATTERN),
    {
      cwd: projectFolder,
    }
  );

  const customElements = await Promise.all(
    customElementsPaths.map((customElementPath) =>
      loadCustomElementComponent(
        customElementPath,
        projectFolder,
        wixFrontendDependencies
      )
    )
  );

  for (const { tagName } of customElements) {
    const result = validateCustomElementName('Site widget')(tagName);

    if (!result.valid) {
      throw new CliError({
        code: CliErrorCode.InvalidCustomElementTagName({
          tagName,
          errorMessage: result.reason,
        }),
        cause: null,
      });
    }
  }

  return customElements;
}

export const BLOCKS_WIDGETS_PATTERN = join(
  BLOCKS_WIDGETS_DIR,
  '**',
  'widget.{ts,tsx,js,jsx}'
);

export const BLOCKS_WIDGETS_PANELS_PATTERN = join(
  BLOCKS_WIDGETS_DIR,
  '**',
  PANEL_PATTERN
);

const BLOCKS_WIDGET_API_PATTERN = 'api.{ts,js}';

async function loadBlocksWidgetAndPanels(
  blocksWidgetPath: string
): Promise<
  Array<
    DevCenterComponent<'BlocksWidget'> | DevCenterComponent<'BlocksWidgetPanel'>
  >
> {
  const { dir: blocksWidgetFolderPath } = parse(blocksWidgetPath);
  const blocksWidgetConfig =
    await loadBlocksWidgetComponentConfig(blocksWidgetPath);

  const blocksWidgetAPIPath = (
    await globby(normalizeGlobbyPattern(BLOCKS_WIDGET_API_PATTERN), {
      cwd: blocksWidgetFolderPath,
    })
  )[0];

  if (!blocksWidgetAPIPath) {
    throw new CliError({
      code: CliErrorCode.BlocksWidgetApiNotFound({
        folderPath: blocksWidgetFolderPath,
      }),
      cause: null,
    });
  }

  const blocksWidgetPanelPaths = await globby(
    normalizeGlobbyPattern(PANEL_PATTERN),
    {
      cwd: blocksWidgetFolderPath,
    }
  );

  const blocksWidgetPanels = await Promise.all(
    blocksWidgetPanelPaths.map(async (panelPath) => {
      const relativePath = normalizeGlobbyPattern(
        join(blocksWidgetFolderPath, panelPath)
      );
      const blocksWidgetPanelConfig =
        await loadBlocksWidgetPanelComponentConfig(relativePath);

      return DevCenterComponent.BlocksWidgetPanel({
        relativePath,
        folderName: relativeFolderName(PANELS_DIR, panelPath),
        platforms: [PlatformType.Editor()],
        widgetId: blocksWidgetConfig.id,
        ...blocksWidgetPanelConfig,
      });
    })
  );

  return [
    DevCenterComponent.BlocksWidget({
      ...blocksWidgetConfig,
      relativePath: blocksWidgetPath,
      platforms: [PlatformType.Site(), PlatformType.Editor()],
      apiPath: normalizeGlobbyPattern(
        join(blocksWidgetFolderPath, blocksWidgetAPIPath)
      ),
    }),
    ...blocksWidgetPanels,
  ];
}

export async function loadBlocksComponents(
  projectFolder: string,
  config: WixConfig
): Promise<
  Array<
    | DevCenterComponent<'BlocksWidget'>
    | DevCenterComponent<'BlocksWidgetPanel'>
    | DevCenterComponent<'BlocksMainComponent'>
  >
> {
  const blocksWidgetsPaths = await globby(
    normalizeGlobbyPattern(BLOCKS_WIDGETS_PATTERN),
    {
      cwd: projectFolder,
    }
  );
  const blocksComponents = (
    await Promise.all(
      blocksWidgetsPaths.map((blocksWidgetsPath) =>
        loadBlocksWidgetAndPanels(blocksWidgetsPath)
      )
    )
  ).flat();

  if (config.blocks) {
    const appConfig = await readAppConfig(projectFolder);
    const id = appConfig?.mainBlocksComponentId ?? randomUUID();

    return [
      ...blocksComponents,
      DevCenterComponent.BlocksMainComponent({
        id,
        platforms: [PlatformType.Blocks()],
      }),
    ];
  }

  return blocksComponents;
}

export const EMBEDDED_SCRIPTS_PARAMS_OVERRIDE_PATTERN = join(
  EMBEDDED_SCRIPTS_DIR,
  '**',
  'params.dev.json'
);

async function loadEmbeddedScriptComponent(
  embeddedScriptPath: string,
  dependencies: WixDependency[]
): Promise<DevCenterComponent<'EmbeddedScript'>> {
  const embeddedScriptConfig =
    await loadEmbeddedScriptComponentConfig(embeddedScriptPath);

  const paramsOverride =
    await loadEmbeddedScriptParamsOverride(embeddedScriptPath);

  return DevCenterComponent.EmbeddedScript({
    ...embeddedScriptConfig,
    relativePath: embeddedScriptPath,
    platforms: [PlatformType.Site()],
    paramsOverride,
    dependencies,
  });
}

export async function loadEmbeddedScriptComponents(
  projectFolder: string,
  wixFrontendDependencies: WixDependency[]
): Promise<Array<DevCenterComponent<'EmbeddedScript'>>> {
  const embeddedScriptsPaths = await globby(
    normalizeGlobbyPattern(EMBEDDED_SCRIPTS_PATTERN),
    {
      cwd: projectFolder,
    }
  );

  const embeddedScripts = await Promise.all(
    embeddedScriptsPaths.map((pagePath) =>
      loadEmbeddedScriptComponent(pagePath, wixFrontendDependencies)
    )
  );

  const validateEmbeddedScriptFolder =
    createEmbeddedScriptFolderValidation(embeddedScripts);

  await Promise.all(
    embeddedScripts.map(async ({ relativePath }) => {
      const folderResult = validateEmbeddedScriptFolder(relativePath);
      const htmlResult = await validateEmbeddedScriptHtml(relativePath);

      if (!folderResult.valid) {
        throw new CliError({
          code: CliErrorCode.InvalidEmbeddedScriptFolder({
            path: relativePath,
            errorMessage: folderResult.reason,
          }),
          cause: null,
        });
      }

      if (!htmlResult.valid) {
        throw new CliError({
          code: CliErrorCode.InvalidEmbeddedScriptTemplate({
            templateFilename: relativePath,
            errorMessage: htmlResult.reason,
          }),
          cause: null,
        });
      }
    })
  );

  return embeddedScripts;
}

async function loadSitePluginComponent(
  sitePluginPath: string,
  projectFolder: string,
  wixDependencies: WixDependency[]
): Promise<DevCenterComponent<'SitePlugin'>> {
  const sitePluginConfig = await loadSitePluginComponentConfig(sitePluginPath);

  const tagName = relativeFolderName(
    getSitePluginsDir(projectFolder),
    sitePluginPath
  );

  const sitePluginPanelPath = join(dirname(sitePluginPath), 'panel.tsx');
  const isPanelExists = await pathExists(
    join(projectFolder, sitePluginPanelPath)
  );

  if (
    sitePluginConfig.marketData.logoUrl &&
    RELATIVE_ASSET_PATH_REGEX.test(sitePluginConfig.marketData.logoUrl)
  ) {
    const pluginDir = dirname(sitePluginPath);
    const assetPath = join(
      projectFolder,
      pluginDir,
      sitePluginConfig.marketData.logoUrl
    );
    if (!(await pathExists(assetPath))) {
      throw new CliError({
        code: CliErrorCode.CannotFindConfigReferencedAsset({
          asset: assetPath,
          configPath: `${pluginDir}/plugin.json`,
          field: 'marketData.logoUrl',
        }),
        cause: null,
      });
    }
  }

  return DevCenterComponent.SitePlugin({
    ...sitePluginConfig,
    relativePath: sitePluginPath,
    tagName,
    platforms: [PlatformType.Editor(), PlatformType.Site()],
    dependencies: wixDependencies,
    ...(isPanelExists
      ? // relative paths should be posix style, as they are used in JS for import statements
        { relativePanelPath: normalizeGlobbyPattern(sitePluginPanelPath) }
      : {}),
  });
}

export async function loadSitePluginComponents(
  projectFolder: string,
  wixDependencies: WixDependency[]
): Promise<Array<DevCenterComponent<'SitePlugin'>>> {
  const sitePluginCustomElementsPaths = await globby(
    normalizeGlobbyPattern(SITE_PLUGINS_PLUGIN_PATTERN),
    {
      cwd: projectFolder,
    }
  );

  const sitePlugins = await Promise.all(
    sitePluginCustomElementsPaths.map((sitePluginPath) =>
      loadSitePluginComponent(sitePluginPath, projectFolder, wixDependencies)
    )
  );

  for (const { tagName } of sitePlugins) {
    const result = validateCustomElementName('Site plugin')(tagName);

    if (!result.valid) {
      throw new CliError({
        code: CliErrorCode.InvalidCustomElementTagName({
          tagName,
          errorMessage: result.reason,
        }),
        cause: null,
      });
    }
  }

  return sitePlugins;
}

async function loadSiteComponentElements(
  basePath: string,
  componentId: string,
  elementsDir: string = ELEMENTS_DIR,
  depth = 0
) {
  if (depth >= 10) {
    return {};
  }

  const elementPaths = await globby(normalizeGlobbyPattern(ELEMENT_PATTERN), {
    cwd: basePath,
  });

  const elements: Record<string, SiteComponentElementItem> = {};

  for (const elementPath of elementPaths) {
    const elementName = relative(elementsDir, dirname(elementPath));
    const elementItem = await loadSiteComponentElementConfig(
      basePath,
      elementPath
    );

    if (elementItem.elementType === ElementType.inlineElement) {
      const elementBasePath = join(basePath, dirname(elementPath));
      const presetCssFiles = await globby(
        normalizeGlobbyPattern(PRESET_PATTERN),
        {
          cwd: elementBasePath,
        }
      );

      const presetsCssFiles = new Set(
        presetCssFiles.map((presetCss) => {
          const { name: presetName } = parse(presetCss);
          return presetName;
        })
      );

      for (const presetName in elementItem.inlineElement.presets) {
        if (!presetsCssFiles.has(presetName)) {
          throw new CliError({
            code: CliErrorCode.MissingPresetCssForSiteComponent({
              componentId,
              presetName,
              elementBasePath,
            }),
            cause: null,
          });
        }
      }

      const nestedElements = await loadSiteComponentElements(
        elementBasePath,
        componentId,
        ELEMENTS_DIR,
        depth + 1
      );

      elements[elementName] = {
        ...elementItem,
        inlineElement: {
          ...elementItem.inlineElement,
          ...(Object.keys(nestedElements).length > 0
            ? { elements: nestedElements }
            : {}),
        },
      };
    } else {
      elements[elementName] = elementItem;
    }
  }

  return elements;
}

async function loadSiteComponentAndPanels(
  projectFolder: string,
  relativePath: string
): Promise<
  Array<
    | DevCenterComponent<'SiteComponent'>
    | DevCenterComponent<'SiteComponentPanel'>
  >
> {
  const config = await loadSiteComponentConfig(projectFolder, relativePath);
  const componentRelativeDir = dirname(relativePath);

  const sdkPath = (
    await globby(normalizeGlobbyPattern(SDK_PATTERN), {
      cwd: join(projectFolder, componentRelativeDir),
    })
  ).at(0);

  const previewPath = (
    await globby(normalizeGlobbyPattern(PREVIEW_PATTERN), {
      cwd: join(projectFolder, componentRelativeDir),
    })
  ).at(0);

  const siteComponentPanelPaths = await globby(
    normalizeGlobbyPattern(PANEL_PATTERN),
    {
      cwd: join(projectFolder, componentRelativeDir),
    }
  );

  const elements = await loadSiteComponentElements(
    join(projectFolder, componentRelativeDir),
    config.id
  );

  const siteComponentPanels = await Promise.all(
    siteComponentPanelPaths.map(async (panelPath) => {
      const relativePath = join(componentRelativeDir, panelPath);
      const siteComponentPanelConfig = await loadSiteComponentPanelConfig(
        projectFolder,
        relativePath
      );

      return DevCenterComponent.SiteComponentPanel({
        relativePath,
        platforms: [PlatformType.Editor()],
        ...siteComponentPanelConfig,
      });
    })
  );

  const presets = config.editorElement.presets;

  if (presets) {
    const presetCssFiles = await globby(
      normalizeGlobbyPattern(PRESET_PATTERN),
      {
        cwd: join(projectFolder, componentRelativeDir),
      }
    );

    const presetsCssFiles = new Set(
      presetCssFiles.map((presetCss) => {
        const { name: presetName } = parse(presetCss);
        return presetName;
      })
    );

    for (const presetName in presets) {
      if (!presetsCssFiles.has(presetName)) {
        throw new CliError({
          code: CliErrorCode.MissingPresetCssForSiteComponent({
            componentId: config.id,
            presetName,
          }),
          cause: null,
        });
      }
    }
  }

  return [
    DevCenterComponent.SiteComponent({
      ...config,
      panels: siteComponentPanels,
      relativePath,
      relativeSdkPath: sdkPath && join(componentRelativeDir, sdkPath),
      relativePreviewPath:
        previewPath && join(componentRelativeDir, previewPath),
      platforms: [PlatformType.Editor(), PlatformType.Site()],
      elements,
      presets,
    }),
    ...siteComponentPanels,
  ];
}

export async function loadSiteComponents(
  projectFolder: string
): Promise<
  Array<
    | DevCenterComponent<'SiteComponent'>
    | DevCenterComponent<'SiteComponentPanel'>
  >
> {
  const paths = await globby(normalizeGlobbyPattern(SITE_COMPONENTS_PATTERN), {
    cwd: projectFolder,
  });

  const siteComponentsAndPanels = await Promise.all(
    paths.map((pagePath) => loadSiteComponentAndPanels(projectFolder, pagePath))
  );

  return siteComponentsAndPanels.flat();
}
